import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  fetchRecipeDetail,
  fetchRecipes,
  fetchRecipeSummary,
} from '@/renderer/services/inventoryRecipeService';
import type { InventoryRecipeFilterParams } from '@/renderer/types/inventory';

export function useInventoryRecipes(params: InventoryRecipeFilterParams) {
  return useQuery({
    queryKey: queryKeys.inventory.recipesList(params),
    queryFn: async () => {
      const result = await fetchRecipes(params);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to fetch recipes');
      }
      return result.data;
    },
    enabled: !!params.companyId,
    placeholderData: keepPreviousData,
  });
}

export function useInventoryRecipeDetail(
  companyId: number | null,
  recipeId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.inventory.recipeDetail(companyId ?? 0, recipeId ?? ''),
    queryFn: async () => {
      const result = await fetchRecipeDetail(companyId!, recipeId!);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to fetch recipe');
      }
      return result.data;
    },
    enabled: !!companyId && !!recipeId,
  });
}

export function useInventoryRecipeSummary(
  companyId: number | null,
  outletId: string,
  status: string,
) {
  return useQuery({
    queryKey: queryKeys.inventory.recipeSummary(
      companyId ?? 0,
      outletId,
      status,
    ),
    queryFn: async () => {
      const result = await fetchRecipeSummary(companyId!, outletId, status);
      if (!result.success || !result.data) {
        throw new Error(result.error ?? 'Failed to fetch recipe summary');
      }
      return result.data;
    },
    enabled: !!companyId,
  });
}
